import {
  getRoundedWeight,
  getParcelChargeableWeight,
} from "@/lib/shipment-functions";
import {
  SHIPPING_METHODS,
  ECOMMERCE_EXPRESS_LITE_TRIAL_ENABLED,
} from "@/lib/constants";
import { MAX_STANDARD_PACKAGE } from "@/lib/shipment/constants";

const getPackingChargeV2 = ({ length, width, height }) => {
  const packOversizeLimitV2 = 55;
  const packBaseCostV2 = 3;
  const packSquareMeterRateV2 = 15;
  const packCubicMeterRateV2 = 60;
  const packFragileBufferV2 = 0.75;
  const packOversizeBufferV2 = 0.25;
  const packOversizeAndFragileBufferV2 = 1;

  const oversize =
    length > packOversizeLimitV2 ||
    width > packOversizeLimitV2 ||
    height > packOversizeLimitV2;

  //convert dimensions in cm to m
  length = Math.ceil(length) / 100;
  width = Math.ceil(width) / 100;
  height = Math.ceil(height) / 100;

  const baseCost = packBaseCostV2;
  const cubicCost = length * width * height * packCubicMeterRateV2;
  const squareCost =
    (length * width + width * height + length * height) * packSquareMeterRateV2;
  const oversizeCostV2 = oversize
    ? (baseCost + cubicCost + squareCost) * packOversizeBufferV2
    : 0;

  let totalRegularPackingCost =
    baseCost + cubicCost + squareCost + oversizeCostV2;
  const roundedFragileCost =
    Math.ceil(
      (oversize
        ? totalRegularPackingCost * packOversizeAndFragileBufferV2
        : totalRegularPackingCost * packFragileBufferV2) * 2
    ) / 2;
  totalRegularPackingCost = Math.ceil(totalRegularPackingCost * 2) / 2;
  return {
    regularPackingCost: totalRegularPackingCost,
    fragilePackingCost: roundedFragileCost,
  };
};

/**
 * Packing cost = (
 *  (Base + Vol Wt * Vol Wt Multiplier) +
 *  (Oversized Base + Num Oversized Dims * Oversized Multiplier) if Oversized
 * ) * Fragile Multiplier if Fragile
 */
const getPackingChargeV3 = ({ length, width, height }) => {
  const PACKING_BASE_COST_SGD = 5;
  const PACKAGE_VOL_WEIGHT_MULTIPLIER = 1;
  const PACKING_FRAGILE_MULTIPLIER = 2;
  const PACKING_OVERSIZED_BASE_COST_SGD = 50;
  const PACKING_OVERSIZED_MULTIPLIER = 10;
  const PACKING_OVERSIZED_DIMENSION_CM = 65;

  const volWeight = getRoundedWeight(
    getParcelChargeableWeight({ length, width, height })
  );

  const numOversizedDims = [length, width, height].filter(
    (dim) => dim > PACKING_OVERSIZED_DIMENSION_CM
  ).length;

  const volWeightCost =
    PACKING_BASE_COST_SGD + volWeight * PACKAGE_VOL_WEIGHT_MULTIPLIER;
  const oversizedPackingCost =
    numOversizedDims > 0
      ? PACKING_OVERSIZED_BASE_COST_SGD +
        numOversizedDims * PACKING_OVERSIZED_MULTIPLIER
      : 0;
  const regularPackingCost = volWeightCost + oversizedPackingCost;

  const fragilePackingCost =
    regularPackingCost * (PACKING_FRAGILE_MULTIPLIER - 1);

  return {
    regularPackingCost,
    fragilePackingCost,
  };
};

/**
 * @param {number} length
 * @param {number} width
 * @param {number} height
 * @returns {{
 * regularPackingCost: number,
 * fragilePackingCost: number
 * }}
 */
export const getPackingCharge = ({ length, width, height }) => {
  if (!(length && width && height)) {
    return 0;
  }

  if (process.env.NEXT_PUBLIC_PACKING_CHARGE_VERSION === "V2") {
    return getPackingChargeV2({ length, width, height });
  }
  return getPackingChargeV3({ length, width, height });
};

export const getTotalPackingCost = (packages) => {
  return packages.reduce((total, currentPackage) => {
    switch (currentPackage.packingType) {
      case "Fragile":
        total +=
          currentPackage.fragilePackingCost + currentPackage.regularPackingCost;
        return total;
      case "Need":
        total += currentPackage.regularPackingCost;
        return total;
      default:
        return total;
    }
  }, 0);
};

export const getVerifiedTotalPackingCost = (packages) => {
  // The packingType is not updated on Beanstalk
  // when verifiedPackingCost and verifiedFragilePackingCost are updated.
  // At least for now, erified quote should take packing costs into account, no matter what the packing type is.
  return packages.reduce((total, currentPackage) => {
    total +=
      currentPackage.verifiedFragilePackingCost +
      currentPackage.verifiedPackingCost;
    return total;
  }, 0);
};

/*
 * Check if parcel fits in box
 * @param {object} parcel
 * @param {object} box
 * @returns {boolean}
 */
export const canFitParcelInBox = (parcel, box) => {
  // Sort both the parcel and box dimensions (ascending)
  const [p1, p2, p3] = [
    Number(parcel.length),
    Number(parcel.width),
    Number(parcel.height),
  ].sort((a, b) => a - b);

  const [b1, b2, b3] = [
    Number(box.length),
    Number(box.width),
    Number(box.height),
  ].sort((a, b) => a - b);

  // Check if, in the sorted orientation, parcel fits in box
  return p1 <= b1 && p2 <= b2 && p3 <= b3;
};

export const checkShipmentDimensions = (shipment) => {
  const { packages, shippingMethod } = shipment;

  // Express shipping via FedEx has no dimension restrictions
  if (shippingMethod === SHIPPING_METHODS.EXPRESS.value)
    return {
      isValid: true,
    };

  const [pkg] = packages;
  const pkgDims = {
    length: Number(pkg.length),
    width: Number(pkg.width),
    height: Number(pkg.height),
  };
  const { length, width, height } = pkgDims;
  if (
    shippingMethod === SHIPPING_METHODS.STANDARD.value &&
    !canFitParcelInBox(pkgDims, MAX_STANDARD_PACKAGE)
  ) {
    return {
      isValid: false,
      reason: `Dimensions must be <${MAX_STANDARD_PACKAGE.length}x${MAX_STANDARD_PACKAGE.width}x${MAX_STANDARD_PACKAGE.height}cm`,
    };
  }

  if (
    shippingMethod === SHIPPING_METHODS.POSTAL.value ||
    (ECOMMERCE_EXPRESS_LITE_TRIAL_ENABLED &&
      shippingMethod === SHIPPING_METHODS.STANDARD.value)
  ) {
    const totalDimension = length + width + height;
    if (totalDimension > 90)
      return {
        isValid: false,
        reason: "Total dimension must be <90cm",
      };
    if (Math.max(length, width, height) > 60)
      return {
        isValid: false,
        reason: "Longest side must be <60cm",
      };
  }

  return {
    isValid: true,
  };
};
